import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CenteredLoading from './components/CenteredLoading';

import { PublicRoute, PrivateRoute, AuthRoute, RecordingRoute } from 'utils/routes';
import { LoaderStore } from 'utils/global';
import { useEffect } from 'react';
import { ConfigProvider, theme as antdTheme, App as AntApp } from 'antd';
import enUS from 'antd/locale/en_US';
import { ThemeProvider as GlobalThemeProvider, useTheme } from './utils/global/ThemeContext';

// Auth Imports
const AuthLogin = React.lazy(() => import('pages/Auth/Login'));
const ResetPassword = React.lazy(() => import('pages/Auth/ResetPassword'));
const ResetWithToken = React.lazy(() => import('pages/Auth/ResetWithToken'));
const OnboardingMain = React.lazy(() => import('pages/Auth/OnboardingMain'));
const OnboardingFirstVisit = React.lazy(() => import('pages/Auth/OnboardingFirstVisit'));
const OAuthCallback = React.lazy(() => import('pages/Auth/OAuthCallback'));

// Settings
const Settings = React.lazy(() => import('pages/Settings/Settings'));

// Event
const EventSingle = React.lazy(() => import('pages/Event/Single'));
const EventActiveList = React.lazy(() => import('pages/Event/Active'));

// Broadcast
const BroadcastMy = React.lazy(() => import('pages/Dashboard/My'));
const BroadcastSingle = React.lazy(() => import('pages/Broadcast/Page'));
const ChatPopoutPage = React.lazy(() => import('pages/Broadcast/ChatPopoutPage'));

// Battle
const BattlePage = React.lazy(() => import('pages/Battle'));
const BattleViewerPage = React.lazy(() => import('pages/Battle/ViewerIndex'));

// Experience Composer Page
const ComposerPage = React.lazy(() => import('pages/Broadcast/ComposerPage'));

// Orgs
const OrgList = React.lazy(() => import('pages/Orgs'));

// Orgs
const UserList = React.lazy(() => import('pages/User/List'));
const Moderation = React.lazy(() => import('pages/Moderation'));
const Rules = React.lazy(() => import('pages/Rules/List'));
const ThemeEditor = React.lazy(() => import('pages/Theme/ThemeEditor'));

// Webhooks
const Webhooks = React.lazy(() => import('pages/Webhooks'));

// Web Embed
const WebEmbed = React.lazy(() => import('pages/WebEmbed'));

// Tools Page
const ToolsPage = React.lazy(() => import('pages/Tools'));

// Usage
const UsagePage = React.lazy(() => import('pages/Usage'));

// Uploaded Videos CRUD
const Media = React.lazy(() => import('components/Media'));
const ViewUploadedMedia = React.lazy(() => import('components/Media/View'));

// Clips
const AllClips = React.lazy(() => import('pages/Clips/AllClips'));

// Video Notes (tenant-wide timeline notes table for producers)
const VideoNotes = React.lazy(() => import('pages/Notes'));

// Exception Imports
const Exception404 = React.lazy(() => import('pages/Exception/404'));

// Add this new import for the ClipsPage
const StreamClipsPage = React.lazy(() => import('pages/Clips/StreamClipsPage'));

// Overlay Editor
const OverlayEditor = React.lazy(() => import('pages/OverlayEditor'));

// Notifications
const AdminNotifications = React.lazy(() => import('pages/Notifications/AdminNotifications'));

// Communities
const Communities = React.lazy(() => import('pages/Communities'));
const ChatbotPage = React.lazy(() => import('pages/Communities/chatbot'));
const CommunityDetail = React.lazy(() => import('pages/Communities/CommunityDetail'));
const CommunitySingle = React.lazy(() => import('pages/Communities/Single'));

// Messages
const Messages = React.lazy(() => import('pages/Messages'));

// Post Production Editor
const PostProductionEditor = React.lazy(() => import('pages/PostProductionEditor'));
const NewStudio = React.lazy(() => import('pages/PostProductionEditor/NewStudio'));
// Dedicated Submit-for-Review export page (browser fallback when render jobs
// are disabled). Minimal progress UI — no editor bundle.
const ExportPage = React.lazy(() => import('pages/Export'));
// Headless render target for backend clip exports (loaded by infrastructure
// headless Chrome, never by users). Rendered from App() ahead of every
// provider — see the short-circuit there for why it skips the app shell.
const ExportWorkerPage = React.lazy(() => import('pages/ExportWorker'));

// Recordings
const RecordingPage = React.lazy(() => import('pages/Recording'));

// Helper component to handle theme-dependent configuration
function SuspendedApp({ children }) {
  const { loading, theme } = useTheme();

  if (loading || !theme) {
    return <CenteredLoading tip="Loading theme..." />;
  }

  return children;
}

function ThemedApp() {
  const { theme: currentTheme } = useTheme();

  useEffect(() => {
    // Update page title when theme loads
    if (currentTheme?.app_name) {
      document.title = currentTheme.app_name;
    }
  }, [currentTheme]);

  // Helper function to extract color value (handles both strings and objects with .main)
  const getColorValue = (color, fallback) => {
    if (!color) return fallback;
    if (typeof color === 'string') return color;
    if (typeof color === 'object' && color.main) return color.main;
    return fallback;
  };

  // Convert our theme to Ant Design token format
  const convertThemeToAntd = theme => {
    return {
      token: {
        // Seed Tokens
        colorPrimary: getColorValue(theme?.colors?.primary, '#6927f4'),
        colorError: getColorValue(theme?.colors?.error, '#f5222d'),
        colorWarning: getColorValue(theme?.colors?.warning, '#faad14'),
        colorSuccess: getColorValue(theme?.colors?.success, '#52c41a'),
        colorInfo: getColorValue(theme?.colors?.link, '#1890ff'),
        colorHighlight: getColorValue(theme?.colors?.highlight, '#469578'),
        colorLink: getColorValue(theme?.colors?.link, '#1890ff'),

        // Font
        fontFamily: theme?.typography?.fontFamily?.primary || '"Proxima Nova", Helvetica, Arial, sans-serif',
        fontSize: parseInt(theme?.typography?.fontSize?.small || '14'),

        // Alias Tokens - Text Colors
        colorText: theme?.colors?.text?.primary || 'rgba(255, 255, 255, 0.85)',
        colorTextBase: theme?.colors?.text?.primary || '#ffffff',
        colorTextSecondary: theme?.colors?.text?.secondary || 'rgba(255, 255, 255, 0.65)',
        colorTextTertiary: theme?.colors?.text?.secondary || 'rgba(255, 255, 255, 0.45)',
        colorTextQuaternary: theme?.colors?.disabled || 'rgba(255, 255, 255, 0.25)',
        colorTextDisabled: theme?.colors?.disabled || 'rgba(255, 255, 255, 0.25)',
        colorTextHeading: theme?.colors?.text?.heading || 'rgba(255, 255, 255, 0.90)',

        // Base background color - This affects the overall base background
        colorBgBase: theme?.colors?.background?.base || '#111',
        colorBgBody: theme?.colors?.background?.secondary || '#f0f2f5',

        // Layout colors - Main background gradient
        // Alias Tokens - Background Colors
        colorBgContainer: theme?.colors?.background?.input || '#27262b', // Input background (while testing)
        colorBgElevated: theme?.colors?.background?.modal || '#1a1a1a', // Modal background (while testing)
        colorBgLayout: theme?.colors?.background?.gradient?.[1] || '#000000',
        colorBgSpotlight: theme?.colors?.background?.spotlightmask || 'rgba(10, 10, 10, 0.75)',
        colorBgMask: theme?.colors?.background?.spotlightmask || 'rgba(10, 10, 10, 0.75)',

        // Input backgrounds
        colorBgContainerDisabled: theme?.colors?.background?.inputDisabled || '#1a1a1a',
        colorBgTextHover: theme?.colors?.background?.inputHover || '#2a2a2a',
        colorBgTextActive: theme?.colors?.background?.inputActive || '#1a1a1a',

        // Alias Tokens - Border
        colorBorder: theme?.colors?.border || '#48474D', // Inputs and buttons border
        colorBorderSecondary: theme?.colors?.border2 || 'rgba(40, 40, 40, 0.1)', // Header border
        borderRadius: parseInt(theme?.layout?.borderRadius || '8'),
        borderRadiusLG: parseInt(theme?.layout?.borderRadius || '8'),
        borderRadiusSM: parseInt(theme?.layout?.borderRadiusBase || '4'),

        // Alias Tokens - Fill Colors
        colorFill: theme?.colors?.surface?.quiet || '#22242a',
        colorFillSecondary: theme?.colors?.surface?.level2 || '#1A1A1A',
        colorFillTertiary: theme?.colors?.surface?.level3 || '#2A2A2A',
        colorFillQuaternary: theme?.colors?.surface?.level4 || '#3A3A3A',

        // Box Shadow
        boxShadow:
          theme?.layout?.boxShadow ||
          '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
      },
      components: {
        Input: {
          controlHeight: 44,
          algorithm: true,
        },
        // Select: {
        //   colorBgContainer: theme?.colors?.background?.input || '#27262b',
        //   colorBgContainerDisabled: theme?.colors?.background?.inputDisabled || '#1a1a1a',
        //   colorTextDisabled: theme?.colors?.text?.secondary || 'rgba(255, 255, 255, 0.65)',
        //   algorithm: true,
        // },
        // DatePicker: {
        //   colorBgContainer: theme?.colors?.background?.input || '#27262b',
        //   colorBgContainerDisabled: theme?.colors?.background?.inputDisabled || '#1a1a1a',
        //   colorBgElevated: theme?.colors?.background?.input || '#27262b',
        //   colorTextDisabled: theme?.colors?.text?.secondary || 'rgba(255, 255, 255, 0.65)',
        //   algorithm: true,
        // },
        // Radio: {
        //   colorBgContainer: theme?.colors?.background?.input || '#27262b',
        //   colorBgContainerDisabled: theme?.colors?.background?.input || '#27262b',
        //   colorTextDisabled: theme?.colors?.text?.secondary || 'rgba(255, 255, 255, 0.65)',
        //   algorithm: true,
        //   radioButtonBg: theme?.colors?.background?.input || '#27262b',
        //   radioButtonCheckedBg: theme?.colors?.background?.input || '#27262b',
        //   radioButtonColor: theme?.colors?.text?.primary || 'rgba(255, 255, 255, 0.85)',
        //   radioButtonHoverColor: theme?.colors?.text?.primary || 'rgba(255, 255, 255, 0.85)',
        //   radioButtonPaddingHorizontal: 16,
        // },
        // Modal: {
        //   colorBgElevated: theme?.colors?.background?.modal || '#1a1a1a',
        //   algorithm: true,
        // },
        // Button: {
        //   algorithm: true,
        // }
      },
      algorithm: antdTheme.darkAlgorithm,
    };
  };

  useEffect(() => {
    if (currentTheme) {
      // Convert theme values to Less variables if needed
      const lessVars = Object.keys(currentTheme).reduce((acc, key) => {
        if (typeof currentTheme[key] === 'string') {
          acc[`@${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`] = currentTheme[key];
        }
        return acc;
      }, {});

      window.less?.modifyVars(lessVars).catch(error => {
        console.error('Failed to apply theme variables:', error);
      });
    }
  }, [currentTheme]);

  const convertedTheme = convertThemeToAntd(currentTheme);

  return (
    <ConfigProvider locale={enUS} theme={convertedTheme} key={`theme-${currentTheme?.colors?.primary || 'default'}`}>
      <AntApp>
        <SuspendedApp>
          <Router>
            <Suspense fallback={<CenteredLoading tip="Loading..." />}>
              <Routes>
                {/* Root */}
                <Route path="/" exact element={<Navigate to="/broadcast/my" replace />} />

                {/* Auth */}
                <Route path="/auth" element={<Navigate to="/auth/login" replace />} />

                <Route
                  path="/auth/login"
                  element={
                    <AuthRoute>
                      <AuthLogin />
                    </AuthRoute>
                  }
                />
                <Route
                  path="/auth/reset"
                  element={
                    <AuthRoute>
                      <ResetPassword />
                    </AuthRoute>
                  }
                />
                <Route
                  path="/auth/reset/:token"
                  element={
                    <AuthRoute>
                      <ResetWithToken />
                    </AuthRoute>
                  }
                />
                <Route path="/auth/forgot-password" element={<Navigate to="/auth/reset" replace />} />
                <Route
                  path="/auth/onboarding"
                  element={
                    <AuthRoute>
                      <OnboardingMain />
                    </AuthRoute>
                  }
                />
                <Route
                  path="/auth/onboarding/:token"
                  element={
                    <AuthRoute>
                      <OnboardingFirstVisit />
                    </AuthRoute>
                  }
                />
                <Route
                  path="/login/oauth/callback"
                  element={
                    <AuthRoute>
                      <OAuthCallback />
                    </AuthRoute>
                  }
                />

                {/* Settings */}
                <Route
                  path="/settings"
                  element={
                    <PrivateRoute>
                      <Settings />
                    </PrivateRoute>
                  }
                />

                {/* Developer */}
                <Route
                  path="/webhooks"
                  element={
                    <PrivateRoute>
                      <Webhooks />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/web-embed"
                  element={
                    <PrivateRoute>
                      <WebEmbed />
                    </PrivateRoute>
                  }
                />

                {/* Notifications */}
                <Route
                  path="/notifications/admin"
                  element={
                    <PrivateRoute>
                      <AdminNotifications />
                    </PrivateRoute>
                  }
                />

                {/* Communities */}
                <Route
                  path="/communities"
                  element={
                    <PrivateRoute>
                      <Communities />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/communities/chatbot"
                  element={
                    <PrivateRoute>
                      <ChatbotPage />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/communities/:id"
                  element={
                    <PrivateRoute>
                      <CommunityDetail />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/communities/:id/chat"
                  element={
                    <PrivateRoute>
                      <CommunitySingle />
                    </PrivateRoute>
                  }
                />

                {/* Messages */}
                <Route
                  path="/messages"
                  element={
                    <PrivateRoute>
                      <Messages />
                    </PrivateRoute>
                  }
                />

                {/* Tools (hidden) */}
                <Route
                  path="/tools"
                  element={
                    <PrivateRoute>
                      <ToolsPage />
                    </PrivateRoute>
                  }
                />

                {/* Event */}
                <Route
                  path="/event"
                  element={
                    <PrivateRoute>
                      <Navigate to="/schedule/events" replace />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/event/active"
                  element={
                    <PrivateRoute>
                      <EventActiveList />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/event/:event"
                  element={
                    <PrivateRoute>
                      <EventSingle />
                    </PrivateRoute>
                  }
                />

                {/* Broadcast/Stream */}
                <Route path="/broadcast" element={<Navigate to="/schedule/events" replace />} />
                <Route
                  path="/broadcast/my"
                  element={
                    <PrivateRoute>
                      <BroadcastMy />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/broadcast/:stream"
                  element={
                    <PrivateRoute noHeader={true}>
                      <BroadcastSingle />
                    </PrivateRoute>
                  }
                />

                {/* BR-15965: pop-out live chat window (production review) */}
                <Route
                  path="/broadcast/:stream/chat"
                  element={
                    <PrivateRoute noHeader={true}>
                      <ChatPopoutPage />
                    </PrivateRoute>
                  }
                />

                {/* Battle */}
                <Route
                  path="/battle/:streamId"
                  element={
                    <PrivateRoute noHeader={true}>
                      <BattlePage />
                    </PrivateRoute>
                  }
                />

                {/* Experience Composer Page */}
                <Route
                  path="/battle-viewer"
                  element={
                    <PublicRoute ecPage={true}>
                      <BattleViewerPage />
                    </PublicRoute>
                  }
                />

                {/* Experience Composer Page */}
                <Route
                  path="/ec"
                  element={
                    <PublicRoute ecPage={true}>
                      <ComposerPage />
                    </PublicRoute>
                  }
                />

                {/* Orgs */}
                <Route
                  path="/orgs/list"
                  element={
                    <PrivateRoute>
                      <OrgList />
                    </PrivateRoute>
                  }
                />

                {/* Hosts */}
                <Route
                  path="/user/list"
                  element={
                    <PrivateRoute>
                      <UserList />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/moderation"
                  element={
                    <PrivateRoute>
                      <Moderation />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/rules"
                  element={
                    <PrivateRoute>
                      <Rules />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/theme"
                  element={
                    <PrivateRoute>
                      <ThemeEditor />
                    </PrivateRoute>
                  }
                />

                {/* Usage */}
                <Route
                  path="/usage"
                  element={
                    <PrivateRoute>
                      <UsagePage />
                    </PrivateRoute>
                  }
                />

                {/* Uploaded Videos */}
                <Route
                  path="/media"
                  element={
                    <PrivateRoute>
                      <Media />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/videos/:id"
                  element={
                    <PrivateRoute>
                      <ViewUploadedMedia type="video" />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/images/:id"
                  element={
                    <PrivateRoute>
                      <ViewUploadedMedia type="image" />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/overlays/:id"
                  element={
                    <PrivateRoute>
                      <ViewUploadedMedia type="overlay" />
                    </PrivateRoute>
                  }
                />

                {/* Clips */}
                <Route
                  path="/allclips"
                  element={
                    <PrivateRoute>
                      <AllClips />
                    </PrivateRoute>
                  }
                />

                {/* Video Notes */}
                <Route
                  path="/notes"
                  element={
                    <PrivateRoute>
                      <VideoNotes />
                    </PrivateRoute>
                  }
                />

                {/* Exceptions */}
                <Route path="*" element={<Exception404 />} />

                {/* Update the Clips route to include the streamId parameter */}
                <Route
                  path="/clips/:streamId"
                  element={
                    <PrivateRoute>
                      <StreamClipsPage />
                    </PrivateRoute>
                  }
                />

                {/* Overlay Editor */}
                <Route
                  path="/overlay-editor"
                  element={
                    <PrivateRoute>
                      <OverlayEditor />
                    </PrivateRoute>
                  }
                />

                {/* /editor/new — create a blank studio and redirect to
                    /editor/:_id. Declared before the dynamic :studioId
                    route to avoid getting captured as the param. */}
                <Route
                  path="/editor/new"
                  element={
                    <PrivateRoute>
                      <NewStudio />
                    </PrivateRoute>
                  }
                />

                {/* Post Production Editor — entered by studio _id. The seed
                    source (stream / recording / blank) lives on studio.sourceId
                    metadata; the URL identifies a studio, not its source. */}
                <Route
                  path="/editor/:studioId"
                  element={
                    <PrivateRoute>
                      <PostProductionEditor />
                    </PrivateRoute>
                  }
                />

                {/* Browser-side Submit-for-Review export — dedicated page so
                    the editor (and its CE.SDK heap) is fully unloaded first.
                    Used when backend render jobs are disabled (503). */}
                <Route
                  path="/export/:studioId"
                  element={
                    <PrivateRoute noHeader={true}>
                      <ExportPage />
                    </PrivateRoute>
                  }
                />

                {/* Recordings */}
                <Route
                  path="/recordings/:recordingId"
                  element={
                    <PrivateRoute noHeader={true}>
                      <RecordingRoute>
                        <RecordingPage />
                      </RecordingRoute>
                    </PrivateRoute>
                  }
                />
              </Routes>
            </Suspense>
          </Router>
        </SuspendedApp>
      </AntApp>
    </ConfigProvider>
  );
}

function App() {
  // The headless export worker route is driven by infrastructure Chrome, not
  // a person: it must render even when the theme/auth backends are slow or
  // down (a theme fetch timeout would otherwise pin the page on "Loading
  // theme..." and the render job would die waiting). Short-circuit before any
  // provider — the page needs no theme, no router, no app shell.
  if (window.location.pathname === '/export-worker') {
    return (
      <Suspense fallback={null}>
        <ExportWorkerPage />
      </Suspense>
    );
  }

  return (
    <LoaderStore>
      <GlobalThemeProvider>
        <ThemedApp />
      </GlobalThemeProvider>
    </LoaderStore>
  );
}

export default App;
